import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  TreatmentPlanPages,
  TreatmentPlanStatus,
} from '../../components/layout/treatment-plan/constants/general';
import { API_ENDPOINT } from '../../constants';
import { fetchWrapper, history } from '../../helpers';

import type { Moment } from 'moment';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  ITreatmentPlan,
  ITreatmentPlanBages,
  ITreatmentPlanStatusIdByLabel,
  ITreatmentPlanGoal,
  ITreatmentPlanStatus,
  ITPPriorAuthorizations,
  IActivationLog,
} from '../../utils/types';

const name = 'treatmentPlans';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<TreatmentPlanState>) =>
    createExtraReducers(builder),
});

export interface TreatmentPlanState {
  list: Array<ITreatmentPlan>;
  current: ITreatmentPlan | null;
  listOfConcludedTreatmentPlans: Array<ITreatmentPlan>;
  error: any;
  loading: boolean;
  activationLogLoading: boolean;
  statuses: Array<ITreatmentPlanStatus>;
  tpStatusIdByLabel: ITreatmentPlanStatusIdByLabel;
  tpPriorAuthorizations: ITPPriorAuthorizations[];
  tpPriorAuthorizationsLoading: boolean;
  badges: ITreatmentPlanBages;
  activeTreatmentPlan: ITreatmentPlan | null;
  goalsOfAllConcludedTreatmentPlans: Array<ITreatmentPlanGoal>;
  tpStatusLoading: boolean;
  currentTpLoading: boolean;
  currentTpRequestId: string | null;
}

type TreatmentPlanStateUpdate = {
  [Key in keyof TreatmentPlanState]: { key: Key; value: TreatmentPlanState[Key] };
}[keyof TreatmentPlanState];

function createInitialState(): TreatmentPlanState {
  return {
    list: [],
    current: null,
    listOfConcludedTreatmentPlans: [],
    loading: false,
    activationLogLoading: false,
    error: null,
    tpPriorAuthorizations: [],
    tpPriorAuthorizationsLoading: false,
    statuses: [], //in the future we should remove this from the slice and only use tpStatusIdByLabel
    tpStatusIdByLabel: {
      [TreatmentPlanStatus.Completed]: undefined,
      [TreatmentPlanStatus.Started]: undefined,
      [TreatmentPlanStatus.Editing]: undefined,
      [TreatmentPlanStatus.Readonly]: undefined,
    },
    badges: {
      [TreatmentPlanPages.ClientInfo]: 0,
      [TreatmentPlanPages.MedInfo]: 0,
      [TreatmentPlanPages.Medications]: 0,
      [TreatmentPlanPages.History]: 0,
      [TreatmentPlanPages.Background]: 0,
      [TreatmentPlanPages.Assessment]: 0,
      [TreatmentPlanPages.CrisisPlan]: 0,
      [TreatmentPlanPages.Goals]: 0,
      [TreatmentPlanPages.DischargeTransition]: 0,
      [TreatmentPlanPages.TreatmentRecommendation]: 0,
    },
    activeTreatmentPlan: null,
    goalsOfAllConcludedTreatmentPlans: [],
    tpStatusLoading: false,
    currentTpLoading: false,
    currentTpRequestId: null,
  };
}

function createReducers() {
  return {
    clearTreatmentPlanState: clearTreatmentPlanState,
    updateState: updateState,
    clearTreatmentPlan: clearTreatmentPlan,
    updateBadge: updateBadge,
    changeTreatmentPlan: changeTreatmentPlan,
    updateTreatmentPlanStatus: updateTreatmentPlanStatus,
  };

  function clearTreatmentPlanState(state: TreatmentPlanState) {
    clearTreatmentPlan(state);
    state.list = [];
    state.listOfConcludedTreatmentPlans = [];
    state.goalsOfAllConcludedTreatmentPlans = [];
    state.tpPriorAuthorizations = [];
    state.tpPriorAuthorizationsLoading = false;
  }

  function updateState(state: TreatmentPlanState, action: PayloadAction<TreatmentPlanStateUpdate>) {
    const { key, value } = action.payload;
    Object.assign(state, { [key]: value });
  }

  function changeTreatmentPlan(state: TreatmentPlanState, action: any) {
    const current = state.list?.find((tp) => tp.id === action.payload);
    if (current) {
      state.current = current;
    }
  }

  function clearTreatmentPlan(state: TreatmentPlanState) {
    state.current = null;
    state.activeTreatmentPlan = null;
    state.badges = {
      [TreatmentPlanPages.ClientInfo]: 0,
      [TreatmentPlanPages.MedInfo]: 0,
      [TreatmentPlanPages.Medications]: 0,
      [TreatmentPlanPages.History]: 0,
      [TreatmentPlanPages.Background]: 0,
      [TreatmentPlanPages.Assessment]: 0,
      [TreatmentPlanPages.CrisisPlan]: 0,
      [TreatmentPlanPages.Goals]: 0,
      [TreatmentPlanPages.DischargeTransition]: 0,
      [TreatmentPlanPages.TreatmentRecommendation]: 0,
    };
  }

  function updateBadge(state: TreatmentPlanState, action: any) {
    state.badges = {
      ...state.badges,
      ...action.payload,
    };
  }

  function updateTreatmentPlanStatus(state: TreatmentPlanState, action: any) {
    const { statusId } = action.payload;
    state.current = { ...state.current, status_id: statusId };
    state.list = state.list.map((tp) =>
      tp.id === state.current?.id ? { ...tp, status_id: statusId } : tp,
    );
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    createTreatmentPlan: createTreatmentPlan(),
    activateTreatmentPlan: activateTreatmentPlan(),
    deactivateTreatmentPlan: deactivateTreatmentPlan(),
    getTreatmentPlanById: getTreatmentPlanById(),
    getTPPriorAuthorizations: getTPPriorAuthorizations(),
    getTreatmentPlanByQuery: getTreatmentPlanByQuery(),
    stopTreatmentPlan: stopTreatmentPlan(),
    getTreatmentPlanStatuses: getTreatmentPlanStatuses(),
    editTreatmentPlan: editTreatmentPlan(),
    downloadTreatmentPlan: downloadTreatmentPlan(),
    getGoalsOfAllConcludedTreatmentPlans: getGoalsOfAllConcludedTreatmentPlans(),
    updateTreatmentPlan: updateTreatmentPlan(),
    regenerateTreatmentPlanPDF: regenerateTreatmentPlanPDF(),
    reassignTreatmentPlan: reassignTreatmentPlan(),
    updatePriorAuthorizationNumber2: updatePriorAuthorizationNumber2(),
    migrateTreatmentPlan: migrateTreatmentPlan(),
    deleteTreatmentPlan: deleteTreatmentPlan(),
    updateTPActivationLogs: updateTPActivationLogs(),
  };

  function createTreatmentPlan() {
    return createAsyncThunk(
      `${name}/createTreatmentPlan`,
      async ({ data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function activateTreatmentPlan() {
    return createAsyncThunk(
      `${name}/activateTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post({
          url: `${baseUrl}/${treatmentPlanId}/activate/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function deactivateTreatmentPlan() {
    return createAsyncThunk(
      `${name}/deactivateTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post({
          url: `${baseUrl}/${treatmentPlanId}/deactivate/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getTPPriorAuthorizations() {
    return createAsyncThunk(
      `${name}/getTPPriorAuthorizations`,
      async ({ pa_expires_within_days, getAccessTokenSilently, client_id }: any) => {
        let url = `${baseUrl}/prior_authorizations/?pa_expires_within_days=${pa_expires_within_days}`;
        if (client_id) {
          url += `&client_id=${client_id}`;
        }
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getTreatmentPlanById() {
    return createAsyncThunk(
      `${name}/getTreatmentPlanById`,
      async ({ treatmentPlanId, expand, getAccessTokenSilently }: any) => {
        let url = `${baseUrl}/${treatmentPlanId}/`;

        // If expand is provided and not empty, append each as a separate query parameter
        if (expand && expand.length > 0) {
          const subResourcesQuery = expand
            .map((resource: string) => `expand=${encodeURIComponent(resource)}`)
            .join('&');
          url += `?${subResourcesQuery}`;
        }
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getTreatmentPlanStatuses() {
    return createAsyncThunk(
      `${name}/getTreatmentPlanStatuses`,
      async ({ getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${API_ENDPOINT}/treatment_plan_statuses/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getTreatmentPlanByQuery() {
    return createAsyncThunk(
      `${name}/getTreatmentPlanByQuery`,
      async ({ query, getAccessTokenSilently }: any) => {
        const queryParams = [];
        if (Object.hasOwn(query, 'clientId')) {
          queryParams.push(`client_id=${query.clientId}`);
        }
        if (Object.hasOwn(query, 'with_activation_logs')) {
          queryParams.push(`with_activation_logs=${query.with_activation_logs}`);
        }
        if (Object.hasOwn(query, 'status')) {
          queryParams.push(`status=${query.status}`);
        }
        if (Object.hasOwn(query, 'expand')) {
          queryParams.push(`expand=${query.expand}`);
        }
        if (Object.hasOwn(query, 'action_required')) {
          queryParams.push(`action_required=${query.action_required}`);
        }
        const queryStr = queryParams.join('&');
        const url = `${baseUrl}/${queryStr ? `?${queryStr}` : ''}`;
        const response = await fetchWrapper.get({
          url,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function stopTreatmentPlan() {
    return createAsyncThunk(
      `${name}/stopTreatmentPlan`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/stop/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function editTreatmentPlan() {
    return createAsyncThunk(
      `${name}/editTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put({
          url: `${baseUrl}/${treatmentPlanId}/edit/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function downloadTreatmentPlan() {
    return createAsyncThunk(
      `${name}/downloadTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/download/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getGoalsOfAllConcludedTreatmentPlans() {
    return createAsyncThunk(
      `${name}/getGoalsOfAllConcludedTreatmentPlans`,
      async (
        { treatmentPlanIds: _treatmentPlanIds, getAccessTokenSilently }: any,
        { getState },
      ) => {
        const state: any = getState();
        const concludedTps = state?.treatmentPlans?.listOfConcludedTreatmentPlans;
        const promises = concludedTps
          ?.map((tp: ITreatmentPlan) => tp?.id)
          .map((treatmentPlanId: number) =>
            fetchWrapper.get({
              url: `${baseUrl}/${treatmentPlanId}/goals/`,
              getAccessTokenSilently,
            }),
          );

        const response = await Promise.all(promises);
        return response;
      },
    );
  }

  function updateTreatmentPlan() {
    return createAsyncThunk(
      `${name}/updateTreatmentPlan`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function reassignTreatmentPlan() {
    return createAsyncThunk(
      `${name}/reassignTreatmentPlan`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/reassign_provider/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function regenerateTreatmentPlanPDF() {
    return createAsyncThunk(
      `${name}/regenerateTreatmentPlanPDF`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.put({
          url: `${baseUrl}/${treatmentPlanId}/pdf_regenerate/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updatePriorAuthorizationNumber2() {
    return createAsyncThunk(
      `${name}/updatePriorAuthorizationNumber2`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/prior_authorization_2/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function migrateTreatmentPlan() {
    return createAsyncThunk(
      `${name}/migrateTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently, data }: any) => {
        const response = await fetchWrapper.put(
          {
            url: `${baseUrl}/${treatmentPlanId}/migrate/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteTreatmentPlan() {
    return createAsyncThunk(
      `${name}/deleteTreatmentPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function updateTPActivationLogs() {
    return createAsyncThunk(
      `${name}/updateTPActivationLogs`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/activation_logs/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<TreatmentPlanState>) {
  const {
    createTreatmentPlan,
    activateTreatmentPlan,
    deactivateTreatmentPlan,
    getTreatmentPlanById,
    getTreatmentPlanByQuery,
    stopTreatmentPlan,
    getTreatmentPlanStatuses,
    editTreatmentPlan,
    downloadTreatmentPlan,
    getGoalsOfAllConcludedTreatmentPlans,
    updateTreatmentPlan,
    reassignTreatmentPlan,
    regenerateTreatmentPlanPDF,
    getTPPriorAuthorizations,
    migrateTreatmentPlan,
    deleteTreatmentPlan,
    updateTPActivationLogs,
  } = extraActions;

  builder
    // createTreatmentPlan()
    .addCase(createTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createTreatmentPlan.fulfilled,
      (state: TreatmentPlanState, action: PayloadAction<ITreatmentPlan>) => {
        state.loading = false;
        state.list = state.list ? [action.payload, ...state.list] : [action.payload];
        state.current = action.payload;
        if (state.current) {
          history.navigate(`/treatment-plan/${state.current.id}/client-info`);
        }
      },
    )
    .addCase(createTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // activateTreatmentPlan()
    .addCase(activateTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(activateTreatmentPlan.fulfilled, (state: TreatmentPlanState, action: any) => {
      state.loading = false;
      state.list = state.list.map((tp: ITreatmentPlan) => {
        if (tp.id === action.meta.arg.treatmentPlanId) {
          tp.is_active = true;
        }
        return tp;
      });
      const newlyActivatedTP = state.list.find(
        (tp: ITreatmentPlan) => tp.id === action.meta.arg.treatmentPlanId,
      );
      state.activeTreatmentPlan = newlyActivatedTP
        ? { ...newlyActivatedTP, is_active: true }
        : null;
      if (state.current?.id === action.meta.arg.treatmentPlanId) {
        state.current = state.current ? { ...state.current, is_active: true } : state.current;
      }
    })
    .addCase(activateTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // deactivateTreatmentPlan()
    .addCase(deactivateTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deactivateTreatmentPlan.fulfilled, (state: TreatmentPlanState, action: any) => {
      state.loading = false;
      state.list = state.list.map((tp: ITreatmentPlan) => {
        if (tp.id === action.meta.arg.treatmentPlanId) {
          tp.is_active = false;
        }
        return tp;
      });
      if (state.activeTreatmentPlan?.id === action.meta.arg.treatmentPlanId) {
        state.activeTreatmentPlan = null;
      }
      if (state.current?.id === action.meta.arg.treatmentPlanId) {
        state.current = state.current ? { ...state.current, is_active: false } : state.current;
      }
    })
    .addCase(deactivateTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getTreatmentPlanById()
    .addCase(getTreatmentPlanById.pending, (state: TreatmentPlanState, action: any) => {
      state.currentTpLoading = true;
      state.currentTpRequestId = action.meta.requestId;
      state.error = null;
    })
    .addCase(getTreatmentPlanById.fulfilled, (state: TreatmentPlanState, action: any) => {
      if (action.meta.requestId !== state.currentTpRequestId) return;
      state.currentTpLoading = false;
      state.current = action.payload;
      state.list = state.list?.map((tp: ITreatmentPlan) =>
        tp.id === action.meta.arg.treatmentPlanId ? action.payload : tp,
      );
    })
    .addCase(getTreatmentPlanById.rejected, (state: TreatmentPlanState, action: any) => {
      if (action.meta.requestId !== state.currentTpRequestId) return;
      state.error = action.error;
      state.currentTpLoading = false;
      toast.error(state.error.message);
    })

    // regenerateTreatmentPlanPDF()
    .addCase(regenerateTreatmentPlanPDF.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(regenerateTreatmentPlanPDF.fulfilled, (state: TreatmentPlanState, _action: any) => {
      state.loading = false;
      toast.warning('PDF Regeneration in progress. This may take a few minutes.');
    })
    .addCase(regenerateTreatmentPlanPDF.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getTPPriorAuthorizations()
    .addCase(getTPPriorAuthorizations.pending, (state: TreatmentPlanState) => {
      state.tpPriorAuthorizationsLoading = true;
      state.error = null;
    })
    .addCase(getTPPriorAuthorizations.fulfilled, (state: TreatmentPlanState, action: any) => {
      state.tpPriorAuthorizations =
        action.payload?.sort((a: any, b: any) => a.days_until_expiry - b.days_until_expiry) ?? [];
      state.tpPriorAuthorizationsLoading = false;
    })
    .addCase(getTPPriorAuthorizations.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.tpPriorAuthorizationsLoading = false;
      toast.error(state.error.message);
    })

    // getTreatmentPlanByQuery()
    .addCase(getTreatmentPlanByQuery.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getTreatmentPlanByQuery.fulfilled, (state: TreatmentPlanState, action: any) => {
      // Get latest started treatment plan
      const sorted = action.payload.sort((a: any, b: any) => {
        return (
          new Date(b['date_treatment_plan_started']).getTime() -
          new Date(a['date_treatment_plan_started']).getTime()
        );
      });
      state.list = sorted;
      state.loading = false;

      state.activeTreatmentPlan = action.payload.find(
        (tp: ITreatmentPlan) => tp.is_active,
      ) ?? null;

      if (!action.meta.arg.skipCurrentTpChange && Boolean(sorted.length)) {
        state.current = state.activeTreatmentPlan ? state.activeTreatmentPlan : sorted[0];
      }
      if (sorted.length === 0 && action.meta.arg.fromTP) {
        state.current = null;
        history.navigate(`/treatment-plan`);
      }
      if (state.current && action.meta.arg.fromTP) {
        history.navigate(
          `/treatment-plan/${state.current.id}/${
            action.meta.arg.activeRoute ? action.meta.arg.activeRoute : 'client-info'
          }`,
        );
      }

      // Get list of all concluded plans
      const completedStatusId = state.tpStatusIdByLabel[TreatmentPlanStatus.Completed];
      const readOnlyStatusId = state.tpStatusIdByLabel[TreatmentPlanStatus.Readonly];
      const editingStatusId = state.tpStatusIdByLabel[TreatmentPlanStatus.Editing];

      if (completedStatusId !== undefined) {
        const concludedStatusIds = [completedStatusId, readOnlyStatusId, editingStatusId].filter(
          (statusId): statusId is number => statusId !== undefined,
        );
        const concludedTreatmentPlans = sorted.filter(
          (tp: ITreatmentPlan) =>
            tp.status_id !== undefined && concludedStatusIds.includes(tp.status_id),
        );
        state.listOfConcludedTreatmentPlans = concludedTreatmentPlans;
      }

      state.badges = {
        [TreatmentPlanPages.ClientInfo]: 0,
        [TreatmentPlanPages.MedInfo]: 0,
        [TreatmentPlanPages.Medications]: 0,
        [TreatmentPlanPages.History]: 0,
        [TreatmentPlanPages.Background]: 0,
        [TreatmentPlanPages.Assessment]: 0,
        [TreatmentPlanPages.CrisisPlan]: 0,
        [TreatmentPlanPages.Goals]: 0,
        [TreatmentPlanPages.DischargeTransition]: 0,
        [TreatmentPlanPages.TreatmentRecommendation]: 0,
      };
    })
    .addCase(getTreatmentPlanByQuery.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateTPActivationLogs()
    .addCase(updateTPActivationLogs.pending, (state: TreatmentPlanState) => {
      state.activationLogLoading = true;
      state.error = null;
    })
    .addCase(updateTPActivationLogs.fulfilled, (state: TreatmentPlanState, action: any) => {
      if (state.list?.length) {
        state.list = state.list.map((tp: ITreatmentPlan) => {
          if (tp.id === action.meta.arg.treatmentPlanId) {
            if (!tp.activation_logs?.length) return tp;
            tp.activation_logs = tp.activation_logs.map((log) => {
              return action.payload?.find((newLog: IActivationLog) => newLog.id === log.id) ?? log;
            });
          }
          return tp;
        });
      }
      state.activationLogLoading = false;
      toast.success('Activation period updated successfully');
    })
    .addCase(updateTPActivationLogs.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.activationLogLoading = false;
    })

    // stopTreatmentPlan()
    .addCase(stopTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      stopTreatmentPlan.fulfilled,
      (
        state: TreatmentPlanState,
        action: PayloadAction<
          null,
          string,
          {
            arg: {
              data: {
                signature: string;
                utc_offset_date_end: number;
                date_treatment_plan_ended: Moment;
              };
            };
          }
        >,
      ) => {
        const { signature, utc_offset_date_end, date_treatment_plan_ended } = action.meta.arg.data;
        state.loading = false;
        state.current = {
          ...state.current,
          signature,
          utc_offset_date_end,
          date_treatment_plan_ended,
        };
        toast.success('Treatment plan concluded');
      },
    )
    .addCase(stopTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getTreatmentPlanStatuses()
    .addCase(getTreatmentPlanStatuses.pending, (state: TreatmentPlanState) => {
      state.tpStatusLoading = true;
      state.error = null;
    })
    .addCase(
      getTreatmentPlanStatuses.fulfilled,
      (state: TreatmentPlanState, action: PayloadAction<Array<ITreatmentPlanStatus>>) => {
        state.tpStatusLoading = false;
        state.statuses = action.payload;
        action.payload.forEach((status: ITreatmentPlanStatus) => {
          state.tpStatusIdByLabel = {
            ...state.tpStatusIdByLabel,
            [status.label]: status.id,
          };
        });
      },
    )
    .addCase(getTreatmentPlanStatuses.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.tpStatusLoading = false;
      toast.error(state.error.message);
    })

    // editTreatmentPlan()
    .addCase(editTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(editTreatmentPlan.fulfilled, (state: TreatmentPlanState, action: any) => {
      state.loading = false;
      state.list = state.list.map((tp: ITreatmentPlan) => {
        if (tp.id === action.meta.arg.treatmentPlanId) {
          if (state.tpStatusIdByLabel[TreatmentPlanStatus.Editing] !== undefined) {
            tp.status_id = state.tpStatusIdByLabel[TreatmentPlanStatus.Editing];
          }
        }
        return tp;
      });
      if (
        state.current?.id === action.meta.arg.treatmentPlanId &&
        state.tpStatusIdByLabel[TreatmentPlanStatus.Editing] !== undefined
      ) {
        state.current = state.current
          ? { ...state.current, status_id: state.tpStatusIdByLabel[TreatmentPlanStatus.Editing] }
          : state.current;
      }
    })
    .addCase(editTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    //downloadTreatmentPlan()
    .addCase(downloadTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(downloadTreatmentPlan.fulfilled, (state: TreatmentPlanState) => {
      state.loading = false;
      state.error = null;
      toast.success('Treatment plan downloaded');
    })
    .addCase(downloadTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // updateTreatmentPlan()
    .addCase(updateTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateTreatmentPlan.fulfilled, (state: TreatmentPlanState, action: any) => {
      state.loading = false;
      state.error = null;
      if (state.current?.id === action.payload.id) {
        state.current = action.payload;
      }
    })
    .addCase(updateTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // getGoalsOfAllConcludedTreatmentPlans()
    .addCase(getGoalsOfAllConcludedTreatmentPlans.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getGoalsOfAllConcludedTreatmentPlans.fulfilled,
      (state: TreatmentPlanState, action: PayloadAction<Array<Array<ITreatmentPlanGoal>>>) => {
        let allGoals: ITreatmentPlanGoal[] = [];
        action.payload.forEach((goalsOfTp, index) => {
          const treatmentPlanId = state.listOfConcludedTreatmentPlans[index]?.id;
          const goalsWithTpId = goalsOfTp.map((goal) => ({
            ...goal,
            treatment_plan_id: treatmentPlanId,
          }));
          allGoals = [...allGoals, ...goalsWithTpId];
        });
        state.goalsOfAllConcludedTreatmentPlans = [...allGoals].reverse();
        state.loading = false;
      },
    )
    .addCase(
      getGoalsOfAllConcludedTreatmentPlans.rejected,
      (state: TreatmentPlanState, action: any) => {
        state.error = action.error;
        state.loading = false;
        toast.error(state.error.message);
      },
    )
    .addCase(reassignTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(reassignTreatmentPlan.fulfilled, (state: TreatmentPlanState, _action: any) => {
      state.loading = false;
      state.error = null;
      toast.success('Treatment plan reassigned successfully', {
        toastId: 'tp-id-status-success',
      });
    })
    .addCase(reassignTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message, {
        toastId: 'tp-id-status-error',
      });
    })
    .addCase(migrateTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      migrateTreatmentPlan.fulfilled,
      (state: TreatmentPlanState, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = null;
        state.current = action.payload;
        toast.success('Treatment plan converted successfully');
      },
    )
    .addCase(migrateTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // deleteTreatmentPlan()
    .addCase(deleteTreatmentPlan.pending, (state: TreatmentPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteTreatmentPlan.fulfilled, (state: TreatmentPlanState) => {
      state.loading = false;
      state.current = null;
      state.error = null;
      toast.success('Treatment plan deleted successfully');
    })
    .addCase(deleteTreatmentPlan.rejected, (state: TreatmentPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const treatmentPlanActions = { ...slice.actions, ...extraActions };
export const treatmentPlanReducer = slice.reducer;
